import { useEffect, useState, Fragment } from "react";
import { toast } from "sonner";
import { Receipt, ArrowsClockwise, CheckCircle, Printer, FileText } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";

const money = (n, c) => (n == null ? "—" : `${c || ""} ${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim());

const statusChip = (s) => {
  const m = { released: { bg: "#DCFCE7", bd: "#BBF7D0", fg: "#166534" },
              not_released: { bg: "#FEF3C7", bd: "#FDE68A", fg: "#92400E" },
              partial: { bg: "#FEF3C7", bd: "#FDE68A", fg: "#92400E" },
              none: { bg: "#F1F5F9", bd: "#E2E8F0", fg: "#475569" } }[s] || {};
  return { fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: m.bg, border: `1px solid ${m.bd}`, color: m.fg };
};

const stageColor = { done: "#16A34A", progress: "#D97706", pending: "#CBD5E1" };
// Proforma → PGI → GST invoice track. PGI + GST are read from SAP via API; Proforma is an app-side
// marker (SAP's native proforma is UI-only and not exposed on any API).
function StageTrack({ proforma, pgi, gst }) {
  const steps = [
    { key: "proforma", label: "Proforma", state: proforma, tip: proforma === "done" ? "App proforma generated" : "No app proforma yet (SAP's native proforma is UI-only, not via API)" },
    { key: "pgi", label: "PGI", state: pgi, tip: { done: "Outbound released / goods issued in SAP", progress: "Outbound created, not released", pending: "Outbound not pushed" }[pgi] },
    { key: "gst", label: "GST Inv", state: gst, tip: { done: "GST invoice released (posted)", progress: "GST invoice created, not released", pending: "No GST invoice yet" }[gst] },
  ];
  return (
    <div data-testid="stage-track" style={{ display: "flex", alignItems: "center", gap: 2 }}>
      {steps.map((s, i) => (
        <Fragment key={s.key}>
          <span title={s.tip} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 9, height: 9, borderRadius: 999, background: stageColor[s.state], display: "inline-block", boxShadow: s.state === "done" ? "0 0 0 2px #DCFCE7" : "none" }} />
            <span style={{ fontSize: 10.5, fontWeight: 700, color: s.state === "pending" ? "#94A3B8" : "#334155" }}>{s.label}</span>
          </span>
          {i < steps.length - 1 && <span style={{ color: "#CBD5E1", fontWeight: 800, margin: "0 3px" }}>›</span>}
        </Fragment>
      ))}
    </div>
  );
}

// ---- Printable Indian GST tax invoice (rendered from SAP read-back) ----
function InvoicePrint({ data, plan, onClose }) {
  const inv = (data?.results || []).map((r) => r.full).filter(Boolean);
  const doPrint = () => window.print();
  const P = ({ p, title }) => (
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 800, fontSize: 11, color: "#334155", marginBottom: 3 }}>{title}</div>
      <div style={{ fontSize: 12, color: "#0F172A", fontWeight: 700 }}>{p?.name || "—"}</div>
      <div style={{ fontSize: 11, color: "#475569" }}>{[p?.street, p?.city, p?.region, p?.postal, p?.country].filter(Boolean).join(", ") || "—"}</div>
      <div style={{ fontSize: 11, color: "#475569" }}>GSTIN: <b>{p?.gstin || "—"}</b></div>
    </div>
  );
  return (
    <div data-testid="invoice-print-overlay" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1000, overflow: "auto", padding: 24 }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div className="no-print" style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 10 }}>
          <button data-testid="invoice-print-btn" className="jde-btn jde-btn-primary" onClick={doPrint}><Printer size={14} weight="bold" style={{ marginRight: 6, verticalAlign: "-2px" }} />Print</button>
          <button data-testid="invoice-print-close" className="jde-btn" onClick={onClose}>Close</button>
        </div>
        <div id="print-area" style={{ background: "#fff", padding: 28, borderRadius: 8 }}>
          {inv.length === 0 && <div style={{ color: "#DC2626" }}>No released invoice data yet. Create + Release first.</div>}
          {inv.map((iv, k) => (
            <div key={k} style={{ marginBottom: 34, borderBottom: k < inv.length - 1 ? "2px dashed #CBD5E1" : "none", paddingBottom: 20 }}>
              <div style={{ textAlign: "center", fontWeight: 800, fontSize: 16, letterSpacing: 1 }}>TAX INVOICE</div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, margin: "6px 0 14px" }}>
                <div><b>GST Invoice No:</b> {iv.invoice_id || "—"}<br /><b>Date:</b> {(iv.date || "").slice(0, 10) || "—"}</div>
                <div style={{ textAlign: "right" }}><b>Export Invoice No:</b> {data.export_no || "—"}<br /><b>Dispatch Plan #:</b> {plan?.voc_no}</div>
              </div>
              <div style={{ display: "flex", gap: 20, border: "1px solid #E2E8F0", borderRadius: 6, padding: 12, marginBottom: 12 }}>
                <P p={iv.seller} title="Seller (Bill From)" />
                <P p={iv.bill_to || iv.buyer} title="Buyer (Bill To)" />
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#F1F5F9", textAlign: "left" }}>
                    <th style={{ padding: "6px 8px", border: "1px solid #E2E8F0" }}>#</th>
                    <th style={{ padding: "6px 8px", border: "1px solid #E2E8F0" }}>Product</th>
                    <th style={{ padding: "6px 8px", border: "1px solid #E2E8F0" }}>HSN/SAC</th>
                    <th style={{ padding: "6px 8px", border: "1px solid #E2E8F0" }}>Description</th>
                    <th style={{ padding: "6px 8px", border: "1px solid #E2E8F0", textAlign: "right" }}>Qty</th>
                    <th style={{ padding: "6px 8px", border: "1px solid #E2E8F0", textAlign: "right" }}>Taxable Value</th>
                    <th style={{ padding: "6px 8px", border: "1px solid #E2E8F0", textAlign: "right" }}>IGST %</th>
                    <th style={{ padding: "6px 8px", border: "1px solid #E2E8F0", textAlign: "right" }}>IGST Amt</th>
                    <th style={{ padding: "6px 8px", border: "1px solid #E2E8F0", textAlign: "right" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(iv.items || []).map((it, i) => (
                    <tr key={i}>
                      <td style={{ padding: "6px 8px", border: "1px solid #E2E8F0" }}>{it.id}</td>
                      <td style={{ padding: "6px 8px", border: "1px solid #E2E8F0" }}>{it.product || "—"}</td>
                      <td style={{ padding: "6px 8px", border: "1px solid #E2E8F0" }}>{it.hsn || "—"}</td>
                      <td style={{ padding: "6px 8px", border: "1px solid #E2E8F0" }}>{it.description || "—"}</td>
                      <td style={{ padding: "6px 8px", border: "1px solid #E2E8F0", textAlign: "right" }}>{it.qty} {it.unit}</td>
                      <td style={{ padding: "6px 8px", border: "1px solid #E2E8F0", textAlign: "right" }}>{money(it.net, "")}</td>
                      <td style={{ padding: "6px 8px", border: "1px solid #E2E8F0", textAlign: "right" }}>{it.tax_pct != null ? `${it.tax_pct}%` : "—"}</td>
                      <td style={{ padding: "6px 8px", border: "1px solid #E2E8F0", textAlign: "right" }}>{money(it.tax, "")}</td>
                      <td style={{ padding: "6px 8px", border: "1px solid #E2E8F0", textAlign: "right" }}>{money(it.gross, "")}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 800 }}>
                    <td colSpan={5} style={{ padding: "6px 8px", border: "1px solid #E2E8F0", textAlign: "right" }}>Totals ({iv.currency})</td>
                    <td style={{ padding: "6px 8px", border: "1px solid #E2E8F0", textAlign: "right" }}>{money(iv.total_net, "")}</td>
                    <td style={{ padding: "6px 8px", border: "1px solid #E2E8F0" }}></td>
                    <td style={{ padding: "6px 8px", border: "1px solid #E2E8F0", textAlign: "right" }}>{money(iv.total_tax, "")}</td>
                    <td style={{ padding: "6px 8px", border: "1px solid #E2E8F0", textAlign: "right" }}>{money(iv.total_gross, "")}</td>
                  </tr>
                </tfoot>
              </table>
              <div data-testid="invoice-amount-words" style={{ fontSize: 12, marginTop: 10, padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: 6, background: "#F8FAFC" }}>
                <b>Amount (in words):</b> {iv.amount_in_words || "—"}
              </div>
              <div style={{ fontSize: 11, color: "#64748B", marginTop: 8 }}>Release status: {iv.release_status || "—"} · Consistency: {iv.consistency || "—"}</div>
            </div>
          ))}
        </div>
      </div>
      <style>{`@media print { .no-print{display:none!important} body *{visibility:hidden} #print-area, #print-area *{visibility:visible} #print-area{position:absolute;left:0;top:0;width:100%} }`}</style>
    </div>
  );
}

export default function SapInvoicing() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [store, setStore] = useState({});
  const [printVoc, setPrintVoc] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/sap/invoicing/plans?limit=120"); setRows(r.data || []); }
    catch { toast.error("Failed to load plans"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = async (voc, path, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(`${path}-${voc}`);
    try {
      const r = await api.post(`/sap/invoicing/${voc}/${path}`);
      setStore((s) => ({ ...s, [voc]: r.data }));
      if (r.data.reason) toast.info(`Plan ${voc}: ${r.data.reason}`);
      else if (r.data.ok) toast.success(`Plan ${voc}: ${path} ok`);
      else toast.warning(`Plan ${voc}: ${path} — see detail`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || `${path} failed`); }
    finally { setBusy(null); }
  };

  const openPrint = async (voc) => {
    if (!store[voc]) { try { const r = await api.get(`/sap/invoicing/${voc}`); setStore((s) => ({ ...s, [voc]: r.data })); } catch {} }
    setPrintVoc(voc);
  };

  return (
    <Layout>
      <div style={{ padding: "22px 26px" }} data-testid="sap-invoicing-page">
        <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", display: "flex", alignItems: "center", gap: 10, margin: 0 }}>
          <Receipt size={24} weight="bold" color="#0EA5E9" /> SAP Invoicing
        </h1>
        <p style={{ fontSize: 13, color: "#64748B", margin: "4px 0 18px", maxWidth: 780 }}>
          For dispatch plans whose SAP outbound is <b>released</b>: <b>Create</b> the GST invoice (not released) + stamp the Export Invoice No, then <b>Release</b> to post it. Everything is read back so you can <b>Print</b> the tax invoice here — no need to open SAP.
        </p>
        <button data-testid="inv-reload" className="jde-btn" onClick={load} disabled={loading} style={{ marginBottom: 12 }}>
          <ArrowsClockwise size={15} weight="bold" style={{ marginRight: 6, verticalAlign: "-2px" }} />{loading ? "Loading…" : "Reload"}
        </button>

        <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflowX: "auto", background: "#fff" }}>
          <table style={{ width: "100%", minWidth: 1040, borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#F8FAFC", textAlign: "left", color: "#475569" }}>
                {["Plan #", "Export Inv #", "Customer", "Status Track", "Outbound", "Invoice", "GST Inv #", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "9px 10px", textAlign: h === "Actions" ? "right" : "left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const d = store[p.voc_no];
                const invStatus = d?.status || p.invoice_status;
                const gst = (d?.results || []).map((r) => r.invoice?.invoice_id).filter(Boolean).join(", ") || (p.gst_invoice_ids || []).join(", ") || "—";
                const released = invStatus === "released";
                const pgiState = p.push_status === "released" ? "done" : p.push_status === "pushed" ? "progress" : "pending";
                const gstState = invStatus === "released" ? "done" : (invStatus === "not_released" || invStatus === "partial") ? "progress" : "pending";
                const pfState = p.proforma ? "done" : "pending";
                return (
                  <Fragment key={p.voc_no}>
                    <tr data-testid={`inv-row-${p.voc_no}`} style={{ borderTop: "1px solid #F1F5F9" }}>
                      <td style={{ padding: "8px 10px", fontWeight: 700 }}>{p.voc_no}</td>
                      <td style={{ padding: "8px 10px" }}>{p.inv_no || "—"}</td>
                      <td style={{ padding: "8px 10px", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.customer || "—"}</td>
                      <td style={{ padding: "8px 10px" }}><StageTrack proforma={pfState} pgi={pgiState} gst={gstState} /></td>
                      <td style={{ padding: "8px 10px" }}><span style={statusChip(p.push_status === "released" ? "released" : "none")}>{p.push_status.replace("_", " ")}</span></td>
                      <td style={{ padding: "8px 10px" }}><span style={statusChip(invStatus || "none")}>{(invStatus || "none").replace("_", " ")}</span></td>
                      <td style={{ padding: "8px 10px", fontWeight: 700, color: "#166534" }}>{gst}</td>
                      <td style={{ padding: "8px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <button data-testid={`inv-preview-${p.voc_no}`} className="jde-btn" style={{ marginRight: 6 }} onClick={() => act(p.voc_no, "preview")} disabled={busy === `preview-${p.voc_no}` || p.push_status !== "released"}>{busy === `preview-${p.voc_no}` ? "…" : "Preview"}</button>
                        <button data-testid={`inv-create-${p.voc_no}`} className="jde-btn jde-btn-primary" style={{ marginRight: 6 }} onClick={() => act(p.voc_no, "create", `Create GST invoice for plan ${p.voc_no} as NOT RELEASED (+ stamp Export No)?`)} disabled={busy === `create-${p.voc_no}` || p.push_status !== "released"}>{busy === `create-${p.voc_no}` ? "Creating…" : "Create"}</button>
                        <button data-testid={`inv-release-${p.voc_no}`} className="jde-btn" style={{ marginRight: 6 }} onClick={() => act(p.voc_no, "release", `Release + post the GST invoice for plan ${p.voc_no}? This posts to the ledger.`)} disabled={busy === `release-${p.voc_no}` || !(invStatus === "not_released" || invStatus === "partial")}>{busy === `release-${p.voc_no}` ? "…" : "Release"}</button>
                        <button data-testid={`inv-print-${p.voc_no}`} className="jde-btn" onClick={() => openPrint(p.voc_no)} disabled={!released}><FileText size={13} weight="bold" style={{ marginRight: 4, verticalAlign: "-2px" }} />Print</button>
                      </td>
                    </tr>
                    {d && (
                      <tr style={{ background: "#F8FAFC" }}>
                        <td colSpan={8} style={{ padding: "6px 10px 12px 20px", fontSize: 12, color: "#334155" }}>
                          {d.reason ? <span style={{ color: "#DC2626" }}>{d.reason}</span> : null}
                          {(d.results || []).map((r, i) => (
                            <div key={i} style={{ color: r.ok || r.released ? "#166534" : "#B45309" }}>
                              • delivery {r.delivery_id} · {r.items} item(s){r.invoice?.invoice_id ? ` · invoice ${r.invoice.invoice_id}` : ""}{r.full?.total_gross != null ? ` · gross ${money(r.full.total_gross, r.full.currency)}` : ""}{r.released ? " · released ✓" : (r.stage === "create" && r.ok ? " · not released" : "")}{(r.fault || r.reason) ? ` · ${r.fault || r.reason}` : ""}
                              {(r.notes || []).slice(0, 2).map((n, j) => <div key={j} style={{ color: "#94A3B8", paddingLeft: 12 }}>– {n}</div>)}
                            </div>
                          ))}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {!loading && rows.length === 0 && <tr><td colSpan={8} style={{ padding: 22, textAlign: "center", color: "#94A3B8" }}>No plans.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {printVoc != null && <InvoicePrint data={store[printVoc]} plan={rows.find((r) => r.voc_no === printVoc)} onClose={() => setPrintVoc(null)} />}
    </Layout>
  );
}
